import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useAuth } from "react-oidc-context";
import { createPortal } from "react-dom";
import _ from "lodash";
import { Button, CircularProgress } from "@mui/material";
import { icons } from "../../../svg";
import { useNitroStore } from "../../../store/Store";
import "./mobileSplitFavoriteOriginPanel.css";
import NitroService from "../../../utils/nitro.service";
import {
  createFavorite,
  deleteFavorite,
  persistPeriodPriceMobile,
  toggleSplitMonth,
} from "../../../pages/home/favoriteOrigin/originManagerCrudActions";
import AddOriginColumn, {
  SelectedOrigin,
} from "../../../pages/home/favoriteOrigin/AddOriginColumn";
import {
  ICreatePeriodPriceResponse,
  IFavoriteLocationExtension,
  IFavoriteMonthExtension,
  IFavoritePeriod,
  IFavoritePriceValue,
  IFavoriteSetExtension,
  IMonthSplitResponse,
  OriginDisplayInfo,
  PriceField,
} from "../../../types/IFavoriteOrigins";
import GuidancePanel from "../../guidancePanel";
import IndicationHistoryPanel from "../../../pages/home/favoriteOrigin/IndicationHistoryPanel";
import InsightsPanel from "../../insightPanel";
import AddIndicationPanel from "../../../pages/home/favoriteOrigin/AddIndicationPanel";
import MobileSubFooter from "../multiLocQuotePanel/components/mobileSubFooter";
import FooterMultiLocation from "../multiLocQuotePanel/components/footerMultiLocation";
import SplitOriginRow, { SplitOriginRowHandle } from "./splitOriginRow";
import OccupantPickerModal from "../multiLocQuotePanel/components/OccupantPickerModal";
import { AddMissingProductModalState } from "../../../types/IMultiLocationQuoteMobile";
import {
  submitAddMissingProductForOrigin,
  handleAddMissingProductForOrigin as handleAddMissingProductForOriginUtil,
  getMonthForFavorite,
  getMonthKey,
  getMonthRange,
  toDateString,
} from "./mobileSplitFavoriteOriginUtils";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { GenericDialogPanel } from "../../../code-connect-ui/dialogPanel/GenericDialogPanel";
import { dialogConfigs } from "../../../code-connect-ui/dialogPanel/dialogConfigs";
import { dialogStyles } from "../../../code-connect-ui/dialogPanel/dialogStyles";
import { extendFavorites } from "../../../utils/extendFavorites";

type OriginType = "koch" | "competitor";
type FilterType = "all" | "koch" | "competitor";

interface MobileFavoriteOriginsPanelProps {
  onClose?: () => void;
  onCloseAll?: () => void;
  onShowTapHere?: (show: boolean) => void;
  onFooterSelect?: (tab: any) => void;
  selectedMonth?: string;
  setSelectedMonth?: (value: string) => void;
}

const mergePriceId = (
  price: IFavoritePriceValue | null,
  previousPrice: IFavoritePriceValue | null | undefined,
): IFavoritePriceValue | null =>
  price ? { ...price, id: previousPrice?.id || "" } : price;

const mergeMonthIds = (
  previousMonth: IFavoriteMonthExtension | undefined,
  updatedMonth: IFavoriteMonthExtension,
): IFavoriteMonthExtension => ({
  ...updatedMonth,
  id: previousMonth?.id || "",
  periods: updatedMonth.periods.map((period, index) => {
    const previousPeriod = previousMonth?.periods?.[index];
    if (!period) return null;
    return {
      ...period,
      id: previousPeriod?.id || "",
      posted: mergePriceId(period?.posted || null, previousPeriod?.posted),
      truck: mergePriceId(period?.truck || null, previousPeriod?.truck),
      rail: mergePriceId(period?.rail || null, previousPeriod?.rail),
    };
  }),
});

const MobileSplitFavoriteOriginsPanel: React.FC<
  MobileFavoriteOriginsPanelProps
> = ({
  onClose,
  onCloseAll,
  onShowTapHere,
  onFooterSelect,
  selectedMonth,
  setSelectedMonth,
}) => {
  const [
    globalProduct,
    priorityProducts,
    showGuidancePanel,
    setShowGuidancePanel,
    showIndicationHistoryPanel,
    setShowIndicationHistoryPanel,
    setSelectedFavoriteOrigin,
    refetchIndicationHistory,
    locations,
    setSelectedLocation,
    setTempPinLocation,
    openInsightsPanel,
    setShowAddIndicationHistoryPanel,
    showAddIndicationHistoryPanel,
    showInsightsPanel,
  ] = useNitroStore((state) => [
    state.product,
    state.priorityProducts,
    state.showGuidancePanel,
    state.setShowGuidancePanel,
    state.showIndicationHistoryPanel,
    state.setShowIndicationHistoryPanel,
    state.setSelectedFavoriteOrigin,
    state.refetchIndicationHistory,
    state.locations,
    state.setSelectedLocation,
    state.setTempPinLocation,
    state.openInsightsPanel,
    state.setShowAddIndicationHistoryPanel,
    state.showAddIndicationHistoryPanel,
    state.showInsightsPanel,
  ]);

  const [triggerRefetchQuotes, refetchQuotes] = useNitroCAStore((state) => [
    state.triggerRefetchQuotes,
    state.refetchQuotes,
  ]);

  const [featureFlags, failedNotification] = useNitroStore((state) => [
    state.featureFlags,
    state.failedNotification,
  ]);
  const { multiRailStation } = featureFlags;

  const [setFavoriteOrigins] = useNitroStore((state) => [
    state.setFavoriteOrigins,
  ]);

  const auth = useAuth();
  const user = `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`;

  const selectedProduct = globalProduct || "AMMONIA";

  const [filterType, setFilterType] = useState<FilterType>("all");

  const [isSwitchingProduct, setIsSwitchingProduct] = useState(false);
  const [showProductNotAvailablePanel, setShowProductNotAvailablePanel] =
    useState(false);
  const [pendingOrigin, setPendingOrigin] = useState<{
    origin: SelectedOrigin;
    type: OriginType;
  } | null>(null);
  const [addMissingProductModalState, setAddMissingProductModalState] =
    useState<AddMissingProductModalState | null>(null);
  const [selectedOccupantKeys, setSelectedOccupantKeys] = useState<string[]>(
    [],
  );
  const [submittingAddMissingProduct, setSubmittingAddMissingProduct] =
    useState(false);
  const addMissingProductInFlightRef = useRef(false);
  const addMissingProductCooldownUntilRef = useRef(0);

  const [isAddingKoch, setIsAddingKoch] = useState(false);
  const [isAddingCompetitor, setIsAddingCompetitor] = useState(false);
  const [currentAddingType, setCurrentAddingType] = useState<OriginType | null>(
    null,
  );
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  const [initialProduct, setInitialProduct] = useState(
    globalProduct || priorityProducts[0] || "AMMONIA",
  );
  const [initialKochCount, setInitialKochCount] = useState(0);
  const [initialCompetitorCount, setInitialCompetitorCount] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);

  const [pendingPriceChanges, setPendingPriceChanges] = useState<{
    [originId: string]: {
      [field in PriceField]?: string;
    };
  }>({});
  const debounceTimers = React.useRef<{ [originId: string]: NodeJS.Timeout }>(
    {},
  );

  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const pendingRequestsRef = useRef<Set<Promise<unknown>>>(new Set());
  const [isLeavingPanel, setIsLeavingPanel] = useState(false);

  const trackBackgroundRequest = useCallback((request: Promise<unknown>) => {
    pendingRequestsRef.current.add(request);
    request.finally(() => {
      pendingRequestsRef.current.delete(request);
    });
  }, []);

  const awaitPendingRequestsBeforeNavigate = useCallback(async () => {
    if (pendingRequestsRef.current.size === 0) return;
    setIsLeavingPanel(true);
    try {
      await Promise.allSettled(Array.from(pendingRequestsRef.current));
    } finally {
      setIsLeavingPanel(false);
    }
  }, []);

  const [favoriteOriginsByProduct, setFavoriteOriginsByProduct] = useState<
    IFavoriteSetExtension[]
  >([]);
  const [outboundModesByOriginId, setOutboundModesByOriginId] = useState<
    Record<string, string[]>
  >({});
  const [isLoadingOutboundModes, setIsLoadingOutboundModes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentFetchRef = useRef<string | null>(null);
  const outboundModesFetchRef = useRef(0);
  const overlayRootRef = useRef<HTMLElement | null>(null);

  const fetchFavoriteOrigins = useCallback(
    async (forceRefresh = false, productOverride?: string) => {
      const productToUse = productOverride || selectedProduct;
      if (!user || !productToUse) {
        setFavoriteOriginsByProduct([]);
        setIsLoading(false);
        currentFetchRef.current = null;
        return;
      }
      const fetchKey = `${user}-${productToUse}`;

      if (!forceRefresh && currentFetchRef.current === fetchKey) {
        return;
      }

      setIsLoading(true);
      currentFetchRef.current = fetchKey;

      try {
        const normalized = await NitroService.getFavoritesByMarketer({
          marketer: user,
        });
        const raw = extendFavorites(normalized[0].favoriteSets);

        if (currentFetchRef.current === fetchKey) {
          setFavoriteOriginsByProduct(raw);
        }
      } catch (error) {
        if (currentFetchRef.current === fetchKey) {
          setFavoriteOriginsByProduct([]);
        }
      } finally {
        if (currentFetchRef.current === fetchKey) {
          setIsLoading(false);
          currentFetchRef.current = null;
        }
      }
    },
    [user, selectedProduct],
  );

  useEffect(() => {
    fetchFavoriteOrigins();
  }, [fetchFavoriteOrigins]);

  const previousShowInsightsPanelRef = useRef(showInsightsPanel);
  useEffect(() => {
    if (previousShowInsightsPanelRef.current && !showInsightsPanel) {
      fetchFavoriteOrigins(true);
    }
    previousShowInsightsPanelRef.current = showInsightsPanel;
  }, [showInsightsPanel, fetchFavoriteOrigins]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    let root = document.getElementById("mobile-overlay-root");
    if (!root) {
      root = document.createElement("div");
      root.id = "mobile-overlay-root";
      root.style.position = "fixed";
      root.style.top = "0";
      root.style.left = "0";
      root.style.right = "0";
      root.style.bottom = "0";
      root.style.zIndex = "100000";
      root.style.pointerEvents = "none";
      document.body.appendChild(root);
    }
    overlayRootRef.current = root as HTMLElement;
  }, []);

  const selectedMonthValue = useMemo(() => {
    if (selectedMonth && selectedMonth.length >= 7) {
      return selectedMonth.slice(0, 7);
    }
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0",
    )}`;
  }, [selectedMonth]);

  const { kochOrigins, competitorOrigins } = useMemo(() => {
    const productOrigins =
      favoriteOriginsByProduct.find(
        (o) => o.product.toUpperCase() === selectedProduct.toUpperCase(),
      )?.favoriteLocations || [];

    const koch: IFavoriteLocationExtension[] = [];
    const competitor: IFavoriteLocationExtension[] = [];

    productOrigins.forEach((origin) => {
      if (origin.competitor) {
        competitor.push(origin);
      } else {
        koch.push(origin);
      }
    });

    return {
      kochOrigins: koch,
      competitorOrigins: competitor,
    };
  }, [favoriteOriginsByProduct, selectedProduct]);

  const splitMonthKeys = useMemo(() => {
    const keys = new Set<string>();
    [...kochOrigins, ...competitorOrigins].forEach((fav) => {
      fav.months.forEach((m) => {
        if (m.split) {
          keys.add(getMonthKey(m.month));
        }
      });
    });
    return Array.from(keys);
  }, [kochOrigins, competitorOrigins]);

  useEffect(() => {
    if (favoriteOriginsByProduct.length === 0) return;
    setFavoriteOrigins(favoriteOriginsByProduct);
  }, [favoriteOriginsByProduct, setFavoriteOrigins]);

  const refreshOutboundModes = useCallback(async (locationIds: string[]) => {
    const fetchId = outboundModesFetchRef.current + 1;
    outboundModesFetchRef.current = fetchId;

    if (locationIds.length === 0) {
      setOutboundModesByOriginId({});
      setIsLoadingOutboundModes(false);
      return;
    }

    setIsLoadingOutboundModes(true);

    try {
      const details =
        (await NitroService.getMultiLocationDetails2({ ids: locationIds })) ||
        [];

      const nextOutboundModesByOriginId = details.reduce((acc, location) => {
        if (!location.id) return acc;
        acc[location.id] = location.outboundModes
          .map((mode) => mode.toUpperCase())
          .filter(Boolean);
        return acc;
      }, {} as Record<string, string[]>);

      setOutboundModesByOriginId(nextOutboundModesByOriginId);
    } catch (error) {
      console.error("Failed to refresh origin outbound modes", error);
    } finally {
      if (outboundModesFetchRef.current === fetchId) {
        setIsLoadingOutboundModes(false);
      }
    }
  }, []);

  const locationIdsKey = useMemo(() => {
    const ids = Array.from(
      new Set(
        [...kochOrigins, ...competitorOrigins]
          .map((origin) => origin.location?.id)
          .filter((id) => Boolean(id)),
      ),
    ).sort();
    return ids.join(",");
  }, [kochOrigins, competitorOrigins]);

  useEffect(() => {
    const locationIds = locationIdsKey ? locationIdsKey.split(",") : [];
    refreshOutboundModes(locationIds);
  }, [locationIdsKey, multiRailStation, refetchQuotes, refreshOutboundModes]);

  const updateFavoriteOriginMonthLocally = useCallback(
    (locationId: string, updatedMonth: IFavoriteMonthExtension) => {
      setFavoriteOriginsByProduct((prev) =>
        prev.map((productEntry) => {
          if (
            productEntry.product.toUpperCase() !== selectedProduct.toUpperCase()
          ) {
            return productEntry;
          }
          return {
            ...productEntry,
            favoriteLocations: productEntry.favoriteLocations.map((fav) => {
              if (fav.location.id !== locationId) return fav;
              const monthKey = getMonthKey(updatedMonth.month);
              const existingMonths = fav.months;
              const idx = existingMonths.findIndex((m) => {
                return getMonthKey(m.month) === monthKey;
              });
              const newMonths = [...existingMonths];
              if (idx >= 0) {
                newMonths[idx] = updatedMonth;
              } else {
                newMonths.push(updatedMonth);
              }
              return { ...fav, months: newMonths };
            }),
          };
        }),
      );
    },
    [selectedProduct],
  );

  const favoriteOriginsByProductRef = useRef<IFavoriteSetExtension[]>([]);
  useEffect(() => {
    favoriteOriginsByProductRef.current = favoriteOriginsByProduct;
  }, [favoriteOriginsByProduct]);

  const pendingPricePersistRef = useRef<
    Record<string, Promise<string | undefined>>
  >({});

  const originRowRefs = useRef<Map<string, SplitOriginRowHandle>>(new Map());
  const flushAllOriginRows = useCallback(() => {
    originRowRefs.current.forEach((instance) => instance.flush());
  }, []);

  const applyPeriodPriceId = useCallback(
    (
      locationId: string,
      competitor: boolean,
      monthKey: string,
      periodIndex: number,
      mode: "posted" | "truck" | "rail",
      periodPriceId: string,
    ) => {
      setFavoriteOriginsByProduct((prev) =>
        prev.map((productEntry) => {
          if (
            productEntry.product.toUpperCase() !== selectedProduct.toUpperCase()
          ) {
            return productEntry;
          }
          return {
            ...productEntry,
            favoriteLocations: productEntry.favoriteLocations.map((fav) => {
              if (
                fav.location.id !== locationId ||
                fav.competitor !== competitor
              ) {
                return fav;
              }
              return {
                ...fav,
                months: fav.months.map((m) => {
                  if (getMonthKey(m.month) !== monthKey) return m;
                  const updatedPeriods = m.periods.map((p, idx) => {
                    if (idx !== periodIndex) return p;
                    const currentPrice = p?.[mode];
                    if (!currentPrice) return p;
                    return {
                      ...p,
                      [mode]: { ...currentPrice, id: periodPriceId },
                    };
                  });
                  return {
                    ...m,
                    periods: updatedPeriods,
                    firstPeriod: updatedPeriods[0] ?? null,
                    secondPeriod: updatedPeriods[1] ?? null,
                  };
                }),
              };
            }),
          };
        }),
      );
    },
    [selectedProduct],
  );

  const applySplitMonthResult = useCallback(
    (
      locationId: string,
      competitor: boolean,
      monthKey: string,
      result: IMonthSplitResponse,
    ) => {
      setFavoriteOriginsByProduct((prev) =>
        prev.map((productEntry) => {
          if (
            productEntry.product.toUpperCase() !== selectedProduct.toUpperCase()
          ) {
            return productEntry;
          }
          return {
            ...productEntry,
            favoriteLocations: productEntry.favoriteLocations.map((fav) => {
              if (
                fav.location.id !== locationId ||
                fav.competitor !== competitor
              ) {
                return fav;
              }
              return {
                ...fav,
                months: fav.months.map((m) => {
                  if (getMonthKey(m.month) !== monthKey) return m;
                  const updatedPeriods = m.periods.map((p, idx) => {
                    if (idx !== 0 || !p) return p;
                    result.periodPrices.forEach((price) => {
                      // eslint-disable-next-line no-param-reassign
                      p[price.name] = {
                        id: price.id,
                        price: price.price,
                        showAs: price.showAs,
                        updatedBy: price.updatedBy,
                        updatedAt: "",
                      };
                    });
                    return {
                      ...p,
                      id: result.period.id,
                      startDate: toDateString(result.period.startDate),
                      endDate: toDateString(result.period.endDate),
                    };
                  });
                  return {
                    ...m,
                    id: result.favoriteMonth.id,
                    split: result.favoriteMonth.split,
                    periods: updatedPeriods,
                    firstPeriod: updatedPeriods[0],
                    secondPeriod: updatedPeriods[1],
                  };
                }),
              };
            }),
          };
        }),
      );
    },
    [selectedProduct],
  );

  const persistMonthChange = useCallback(
    (
      favorite: IFavoriteLocationExtension,
      updatedMonth: IFavoriteMonthExtension,
    ) => {
      if (!user || !selectedProduct) return;
      const monthKey = getMonthKey(updatedMonth.month);
      const previousMonth = favorite.months.find(
        (m) => getMonthKey(m.month) === monthKey,
      );
      const locationId = favorite.location.id;
      const { competitor } = favorite;

      if (
        favorite.id &&
        previousMonth?.id &&
        previousMonth.split !== updatedMonth.split
      ) {
        const splitRequest = toggleSplitMonth(
          user,
          previousMonth,
          !updatedMonth.split,
        )
          .then((response) => {
            const result = response?.[0];
            if (result) {
              applySplitMonthResult(locationId, competitor, monthKey, result);
            }
          })
          .catch((error) => {
            console.error("Failed to toggle split month:", error);
            failedNotification(
              "Failed to update split",
              "We could not save the split/unsplit change. Please try again.",
            );
          });
        trackBackgroundRequest(splitRequest);
      }

      if (!favorite.id) {
        return;
      }

      const modes: Array<"posted" | "truck" | "rail"> = [
        "posted",
        "truck",
        "rail",
      ];
      modes.forEach((mode) => {
        updatedMonth.periods.forEach((period, periodIndex) => {
          const previousPeriod = previousMonth?.periods?.[periodIndex];
          const newPrice = period?.[mode];
          const oldPrice = previousPeriod?.[mode];
          const changed =
            (newPrice?.price ?? null) !== (oldPrice?.price ?? null) ||
            (newPrice?.showAs ?? "PRICE") !== (oldPrice?.showAs ?? "PRICE");
          if (!changed) return;

          const priceKey = `${locationId}|${competitor}|${monthKey}|${periodIndex}|${mode}`;
          const previousRequest =
            pendingPricePersistRef.current[priceKey] ||
            Promise.resolve(newPrice?.id);

          const priceRequest = previousRequest
            .catch(() => undefined)
            .then((resolvedId) => {
              const periodForRequest: IFavoritePeriod | null =
                resolvedId && period
                  ? {
                      ...period,
                      [mode]: { ...newPrice, id: resolvedId },
                    }
                  : period;

              return persistPeriodPriceMobile({
                marketer: user,
                product: selectedProduct,
                editedOrigin: favorite,
                monthEntry: updatedMonth,
                period: periodForRequest,
                mode,
              });
            })
            .then((created?: ICreatePeriodPriceResponse) => {
              if (created?.periodPriceId) {
                applyPeriodPriceId(
                  locationId,
                  competitor,
                  monthKey,
                  periodIndex,
                  mode,
                  created.periodPriceId,
                );
                return created.periodPriceId;
              }
              return newPrice?.id;
            })
            .catch((error) => {
              console.error(`Failed to save ${mode} price:`, error);
              failedNotification(
                "Failed to save price",
                `We could not save the ${mode} price change. Please try again.`,
              );
              return undefined;
            });

          pendingPricePersistRef.current[priceKey] = priceRequest;
          trackBackgroundRequest(priceRequest);
        });
      });
    },
    [
      user,
      selectedProduct,
      applySplitMonthResult,
      applyPeriodPriceId,
      trackBackgroundRequest,
      failedNotification,
    ],
  );

  const persistNewFavorite = useCallback(
    (origin: { id: string; name: string }, type: OriginType) => {
      if (!user || !selectedProduct) return;

      const productFavorites =
        favoriteOriginsByProductRef.current.find(
          (o) => o.product.toUpperCase() === selectedProduct.toUpperCase(),
        )?.favoriteLocations || [];
      const order = productFavorites.filter(
        (fav) => fav.competitor === (type === "competitor"),
      ).length;

      const createRequest = createFavorite(
        user,
        selectedProduct,
        {
          id: origin.id,
          location: { id: origin.id, name: origin.name, outboundModes: [] },
          competitor: type === "competitor",
          months: [],
        },
        order,
      )
        .then(() => fetchFavoriteOrigins(true))
        .catch((error) => {
          console.error("Failed to create favorite origin:", error);
          failedNotification(
            "Failed to add origin",
            "We could not save the new origin. Please try again.",
          );
        });

      trackBackgroundRequest(createRequest);
    },
    [
      user,
      selectedProduct,
      fetchFavoriteOrigins,
      trackBackgroundRequest,
      failedNotification,
    ],
  );

  const [hasStartedLoading, setHasStartedLoading] = useState(false);

  useEffect(() => {
    if (isLoading && isSwitchingProduct) {
      setHasStartedLoading(true);
    }
  }, [isLoading, isSwitchingProduct]);

  useEffect(() => {
    if (!isLoading && isSwitchingProduct && hasStartedLoading) {
      setIsSwitchingProduct(false);
      setHasStartedLoading(false);
    }
  }, [isLoading, isSwitchingProduct, hasStartedLoading]);

  useEffect(() => {
    if (!isLoading && user && selectedProduct) {
      setInitialProduct(selectedProduct);
      setInitialKochCount(kochOrigins.length);
      setInitialCompetitorCount(competitorOrigins.length);
      setHasChanges(false);
    }
  }, [isLoading, user, selectedProduct, kochOrigins, competitorOrigins]);

  useEffect(() => {
    const productChanged = selectedProduct !== initialProduct;
    const kochCountChanged = kochOrigins.length !== initialKochCount;
    const competitorCountChanged =
      competitorOrigins.length !== initialCompetitorCount;

    setHasChanges(productChanged || kochCountChanged || competitorCountChanged);
  }, [
    selectedProduct,
    kochOrigins,
    competitorOrigins,
    initialProduct,
    initialKochCount,
    initialCompetitorCount,
  ]);

  useEffect(() => {
    if (kochOrigins.length || competitorOrigins.length) {
      setPendingPriceChanges((prev) => {
        const updated = { ...prev };
        let hasPriceChanges = false;

        Object.entries(prev).forEach(([originId, pendingFields]) => {
          const allOrigins = [...kochOrigins, ...competitorOrigins];
          const backendOrigin = allOrigins.find(
            (o) => o.location.id === originId,
          );
          if (!backendOrigin) return;

          const fieldsToCheck = Object.entries(pendingFields);
          const allFieldsMatch = fieldsToCheck.every(
            ([field, pendingValue]) => {
              const backendValue =
                backendOrigin[`${field}Price` as keyof typeof backendOrigin];
              return String(backendValue ?? "") === pendingValue;
            },
          );

          if (allFieldsMatch && fieldsToCheck.length > 0) {
            delete updated[originId];
            hasPriceChanges = true;
          }
        });

        return hasPriceChanges ? updated : prev;
      });
    }
  }, [kochOrigins, competitorOrigins, selectedProduct]);

  const addOriginLocally = useCallback(
    (origin: { id: string; name: string }, type: OriginType) => {
      const month = selectedMonthValue;
      const { startDate, endDate } = getMonthRange(month);
      const [yearStr, monthStr] = month.split("-");
      const firstPeriod = {
        id: "",
        startDate,
        endDate,
        posted: {
          id: "",
          updatedAt: "",
          updatedBy: "",
          price: null,
          showAs: "PRICE",
        },
        truck: {
          id: "",
          updatedAt: "",
          updatedBy: "",
          price: null,
          showAs: "PRICE",
        },
        rail: {
          id: "",
          updatedAt: "",
          updatedBy: "",
          price: null,
          showAs: "PRICE",
        },
        infos: [],
      } as IFavoritePeriod;
      const newFavorite: IFavoriteLocationExtension = {
        id: "",
        order: 0,
        location: { id: origin.id, name: origin.name, outboundModes: [] },
        competitor: type === "competitor",
        months: [
          {
            id: "",
            parsedMonth: {
              year: Number(yearStr),
              month: Number(monthStr),
              day: 1,
            },
            month: `${yearStr}-${monthStr}-01`,
            split: false,
            firstPeriod,
            secondPeriod: null,
            periods: [firstPeriod],
          },
        ],
      };

      const prev = favoriteOriginsByProductRef.current;
      const hasProduct = prev.some(
        (o) => o.product.toUpperCase() === selectedProduct.toUpperCase(),
      );
      const updated = hasProduct
        ? prev.map((productEntry) => {
            if (
              productEntry.product.toUpperCase() !==
              selectedProduct.toUpperCase()
            )
              return productEntry;
            return {
              ...productEntry,
              favoriteLocations: [
                ...productEntry.favoriteLocations,
                newFavorite,
              ],
            };
          })
        : [
            ...prev,
            {
              id: "",
              product: selectedProduct,
              favoriteLocations: [newFavorite],
            },
          ];

      favoriteOriginsByProductRef.current = updated;
      setFavoriteOriginsByProduct(updated);

      triggerRefetchQuotes();
    },
    [selectedMonthValue, selectedProduct, triggerRefetchQuotes],
  );

  const addOriginLocallyAndPersist = useCallback(
    (origin: { id: string; name: string }, type: OriginType) => {
      addOriginLocally(origin, type);
      persistNewFavorite(origin, type);
    },
    [addOriginLocally, persistNewFavorite],
  );

  const submitAddMissingProductForSelectedOccupants = () =>
    submitAddMissingProductForOrigin({
      addMissingProductModalState,
      selectedOccupantKeys,
      addMissingProductInFlight: addMissingProductInFlightRef,
      addMissingProductCooldownUntil: addMissingProductCooldownUntilRef,
      setSubmittingAddMissingProduct,
      setAddMissingProductModalState,
      setSelectedOccupantKeys,
      failedNotification,
      addOriginLocallyFn: addOriginLocallyAndPersist,
    });

  const handleAddMissingProductForOrigin = () =>
    handleAddMissingProductForOriginUtil({
      pendingOrigin,
      addMissingProductInFlight: addMissingProductInFlightRef,
      addMissingProductCooldownUntil: addMissingProductCooldownUntilRef,
      selectedProduct,
      setShowProductNotAvailablePanel,
      setAddMissingProductModalState,
      setSelectedOccupantKeys,
      failedNotification,
      addOriginLocallyFn: addOriginLocallyAndPersist,
    });

  const handleAddOrigin = (
    origin: SelectedOrigin | undefined,
    type: OriginType,
  ) => {
    if (!origin) {
      setCurrentAddingType(null);
      setIsAddingKoch(false);
      setIsAddingCompetitor(false);
      return;
    }

    if (
      origin.products &&
      !origin.products
        .map((p) => p.toUpperCase())
        .includes(selectedProduct.toUpperCase())
    ) {
      setPendingOrigin({ origin, type });
      setShowProductNotAvailablePanel(true);
      setCurrentAddingType(null);
      setIsAddingKoch(false);
      setIsAddingCompetitor(false);
      return;
    }

    const locationRecord = locations.find(
      (location) => location.id === origin.id,
    );
    const typesToAdd: OriginType[] =
      locationRecord?.pinType === "kochAndCompetitor"
        ? ["koch", "competitor"]
        : [type];

    typesToAdd.forEach((originType) => {
      addOriginLocallyAndPersist(origin, originType);
    });

    setCurrentAddingType(null);
    setIsAddingKoch(false);
    setIsAddingCompetitor(false);
  };

  const handleCloseAddOriginPanel = () => {
    setCurrentAddingType(null);
    setIsAddingKoch(false);
    setIsAddingCompetitor(false);
  };

  const handleRemoveOrigin = (id: string, type: OriginType) => {
    const locationId = id.replace(`${type}-`, "");
    const isCompetitorRow = type === "competitor";

    const favoriteToRemove = [...kochOrigins, ...competitorOrigins].find(
      (fav) =>
        fav.location.id === locationId && fav.competitor === isCompetitorRow,
    );

    setFavoriteOriginsByProduct((prev) =>
      prev.map((productEntry) => ({
        ...productEntry,
        favoriteLocations: productEntry.favoriteLocations.filter(
          (fav) =>
            !(
              fav.location.id === locationId &&
              fav.competitor === isCompetitorRow
            ),
        ),
      })),
    );

    triggerRefetchQuotes();

    if (!user || !selectedProduct || !favoriteToRemove) return;

    const deleteRequest = deleteFavorite(
      user,
      selectedProduct,
      favoriteToRemove,
    ).catch((error) => {
      console.error("Failed to remove origin:", error);
      failedNotification(
        "Failed to remove origin",
        "We could not remove the origin. Please try again.",
      );
    });
    trackBackgroundRequest(deleteRequest);
  };

  const handleMenuAction = async (action: any, origin: OriginDisplayInfo) => {
    const locationId = origin.id.includes("-")
      ? origin.id.substring(origin.id.indexOf("-") + 1)
      : origin.id;
    const fullOrigin =
      action === "remove"
        ? null
        : [...kochOrigins, ...competitorOrigins].find(
            (o) => o.location.id === locationId,
          );
    if (fullOrigin) {
      setSelectedFavoriteOrigin([fullOrigin]);
    }

    switch (action) {
      case "overview": {
        const selectedFavoriteOrigin = _.find(locations, (location) => {
          return location.id === locationId;
        });

        if (
          selectedFavoriteOrigin &&
          selectedFavoriteOrigin.pinType === "zipCodes"
        ) {
          return;
        }

        if (!selectedFavoriteOrigin) {
          console.warn("Overview target not found in locations", {
            locationId,
          });
          try {
            const details = await NitroService.getLocationDetails2(locationId);
            const lat = Number(details?.coordinates?.[0]);
            const lng = Number(details?.coordinates?.[1]);

            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              setTempPinLocation({ lat, lng });
            }
          } catch (err) {
            console.error("Failed to load overview location details", err);
          }

          openInsightsPanel(locationId, "Logistics", false);
          break;
        }
        setSelectedLocation(selectedFavoriteOrigin);
        setTempPinLocation({
          lat: selectedFavoriteOrigin.lat,
          lng: selectedFavoriteOrigin.long,
        });

        openInsightsPanel(locationId, "Logistics", false);
        break;
      }
      case "guidance":
        setShowGuidancePanel(true);
        break;
      case "add-fob":
        setShowAddIndicationHistoryPanel(true);
        break;
      case "history":
        if (fullOrigin) {
          refetchIndicationHistory(
            fullOrigin.location.id,
            fullOrigin.location.name,
          );
        }
        setShowIndicationHistoryPanel(true);
        break;
      case "remove": {
        const originType = origin.id.split("-")[0] as OriginType;
        handleRemoveOrigin(origin.id, originType);
        break;
      }
      default:
        break;
    }
  };

  const renderOriginSection = (
    type: OriginType,
    origins: IFavoriteLocationExtension[],
    isAdding: boolean,
    setIsAdding: (adding: boolean) => void,
  ) => {
    const isKoch = type === "koch";
    const title = isKoch ? "Koch Origins" : "Competitor Origins";

    return (
      <section
        className="origins-section-mobile"
        id={`favorite-origins-section-id-${type}`}
      >
        {origins
          .filter((fav) => fav)
          .map((fav) => {
            const rowType: OriginType = fav.competitor ? "competitor" : "koch";
            const uniqueId = `${rowType}-${fav.location.id}`;
            const pending = pendingPriceChanges[uniqueId] || {};
            const outboundModes =
              outboundModesByOriginId[fav.location.id] ||
              fav.location.outboundModes
                .map((mode) => mode.toUpperCase())
                .filter(Boolean);
            const selectedFavoriteMonth =
              getMonthForFavorite(fav, selectedMonthValue) || undefined;

            const firstPeriod = selectedFavoriteMonth?.periods?.[0];
            const firstTruckPrice = firstPeriod?.truck?.price;
            const firstRailPrice = firstPeriod?.rail?.price;

            return (
              <SplitOriginRow
                key={uniqueId}
                ref={(instance) => {
                  if (instance) {
                    originRowRefs.current.set(uniqueId, instance);
                  } else {
                    originRowRefs.current.delete(uniqueId);
                  }
                }}
                origin={{
                  id: uniqueId,
                  name: fav.location.name,
                  truckMin:
                    pending.truckMin ??
                    (firstTruckPrice === null || firstTruckPrice === undefined
                      ? ""
                      : String(firstTruckPrice)),
                  truckMax:
                    pending.truckMax ??
                    (firstTruckPrice === null || firstTruckPrice === undefined
                      ? ""
                      : String(firstTruckPrice)),
                  railMin:
                    pending.railMin ??
                    (firstRailPrice === null || firstRailPrice === undefined
                      ? ""
                      : String(firstRailPrice)),
                  railMax:
                    pending.railMax ??
                    (firstRailPrice === null || firstRailPrice === undefined
                      ? ""
                      : String(firstRailPrice)),
                }}
                onRemove={(id) => handleRemoveOrigin(id, rowType)}
                onAction={(actionType, originData) =>
                  handleMenuAction(actionType, originData)
                }
                activeMenuId={activeMenuId}
                setActiveMenuId={setActiveMenuId}
                type={rowType}
                onShowTapHere={onShowTapHere}
                outboundModes={outboundModes}
                selectedMonth={selectedFavoriteMonth}
                selectedMonthKey={selectedMonthValue}
                onMonthChange={(month) => {
                  const previousMonth = (fav.months || []).find(
                    (m) => getMonthKey(m.month) === getMonthKey(month.month),
                  );
                  const monthWithIds = mergeMonthIds(previousMonth, month);
                  updateFavoriteOriginMonthLocally(
                    fav.location.id,
                    monthWithIds,
                  );
                  persistMonthChange(fav, monthWithIds);
                }}
              />
            );
          })}

        {!isAdding && origins.length === 0 && (
          <div className="empty-state-container">
            <img
              data-tooltip-id="location-icon"
              src={icons.LocationIcon}
              alt="Location Icon"
              className="empty-state-icon"
            />
            <div className="empty-state-text">
              No {type} origins added yet
              {isKoch && <span style={{ color: "red" }}> (Required)</span>}
            </div>
            <div className="empty-state-subtext">
              Add origin and set FOB price
            </div>
          </div>
        )}

        {!isAdding && (
          <Button
            id={`add-origin-button-id-${type}`}
            className="add-origin-button"
            onClick={() => {
              setIsAdding(true);
              setCurrentAddingType(type);
              setIsAddingCompetitor(type === "competitor");
            }}
          >
            <span
              className="add-origin-text"
              style={{ color: title.includes("Koch") ? "#1D4ED8" : "#DC2626" }}
            >
              + Add {title.slice(0, -1)}
            </span>
          </Button>
        )}
      </section>
    );
  };

  const shouldHideSubFooter =
    showAddIndicationHistoryPanel || showIndicationHistoryPanel;

  return (
    <div
      id="mobile-indication-history-id-2"
      className={`${"mobile-favorite-origins-panel-container"}`}
    >
      <GenericDialogPanel
        open={showProductNotAvailablePanel}
        styleProfile={dialogStyles.bottomSheetWarning}
        {...dialogConfigs.productNotAvailable(selectedProduct)}
        onCancel={() => setShowProductNotAvailablePanel(false)}
        onConfirm={handleAddMissingProductForOrigin}
      />

      <OccupantPickerModal
        modalState={addMissingProductModalState}
        selectedOccupantKeys={selectedOccupantKeys}
        submitting={submittingAddMissingProduct}
        onClose={() => setAddMissingProductModalState(null)}
        onToggleOccupant={(key) =>
          setSelectedOccupantKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
          )
        }
        onSubmit={submitAddMissingProductForSelectedOccupants}
      />

      {showGuidancePanel && (
        <GuidancePanel
          open={showGuidancePanel}
          close={() => setShowGuidancePanel(false)}
        />
      )}
      <InsightsPanel />

      <AddIndicationPanel />
      {showIndicationHistoryPanel && <IndicationHistoryPanel />}

      {currentAddingType && (
        <div className="add-origin-panel">
          <div className="add-origin-panel-content">
            <AddOriginColumn
              onAdd={(origin?: SelectedOrigin) =>
                handleAddOrigin(origin, currentAddingType)
              }
              onCancel={handleCloseAddOriginPanel}
              favoriteOrigins={
                currentAddingType === "koch" ? kochOrigins : competitorOrigins
              }
              context="mobileFavoriteOriginPanel"
              isCompetitor={isAddingCompetitor}
            />
          </div>
        </div>
      )}

      {isLeavingPanel &&
        (() => {
          const overlay = (
            <div
              className="adding-origin-overlay device-wide-overlay"
              style={{ pointerEvents: "auto" }}
            >
              <CircularProgress size="40px" className="adding-origin-spinner" />
              <div className="adding-origin-text">Saving changes...</div>
            </div>
          );
          return overlayRootRef.current
            ? createPortal(overlay, overlayRootRef.current)
            : overlay;
        })()}

      <div
        id="mobile-favorite-origins-content-split"
        className={`${"mobile-favorite-origins-content"}`}
      >
        <div
          id="origin-manager-title-id"
          className="favorite-origin-header-row"
        >
          <Button
            id="favorite-origin-close-back"
            aria-label="Close"
            onClick={async () => {
              flushAllOriginRows();
              triggerRefetchQuotes();

              await awaitPendingRequestsBeforeNavigate();

              if (onClose) {
                onClose();
              } else {
                useNitroStore
                  .getState()
                  .setShowMobileFavoriteOriginPanel(false);
              }

              if (hasChanges) {
                window.location.reload();
              }
            }}
          >
            <img
              data-tooltip-id="go-back-icon"
              src={icons.BackWithBackgroundIcon}
              alt="Go Back Icon"
              className="back-icon-favorite-origin"
            />
          </Button>
          <div
            id="favorite-origins-title-product"
            className="section-title-products"
          >
            Origins Manager
          </div>
        </div>

        <section className="products-section">
          <div
            id="origin-filter-toggle-container"
            className="origin-filter-toggle-container"
          >
            <Button
              className={`origin-filter-btn btn-all ${
                filterType === "all" ? "active" : ""
              }`}
              onClick={() => setFilterType("all")}
            >
              All
            </Button>
            <Button
              className={`origin-filter-btn btn-koch ${
                filterType === "koch" ? "active" : ""
              }`}
              onClick={() => setFilterType("koch")}
            >
              Koch
            </Button>
            <Button
              className={`origin-filter-btn btn-competitor ${
                filterType === "competitor" ? "active" : ""
              }`}
              onClick={() => setFilterType("competitor")}
            >
              Competitor
            </Button>
          </div>
        </section>

        {isLoading &&
          kochOrigins.length === 0 &&
          competitorOrigins.length === 0 &&
          (() => {
            const overlay = (
              <div
                className="adding-origin-overlay"
                style={{ pointerEvents: "auto" }}
              >
                <CircularProgress
                  size="40px"
                  className="adding-origin-spinner"
                />
                <div className="adding-origin-text">
                  Loading favorite origins...
                </div>
              </div>
            );
            return overlayRootRef.current
              ? createPortal(overlay, overlayRootRef.current)
              : overlay;
          })()}

        {isLoadingOutboundModes &&
          (() => {
            const overlay = (
              <div
                className="adding-origin-overlay"
                style={{ pointerEvents: "auto" }}
              >
                <CircularProgress
                  size="40px"
                  className="adding-origin-spinner"
                />
                <div className="adding-origin-text">
                  Loading origin modes...
                </div>
              </div>
            );
            return overlayRootRef.current
              ? createPortal(overlay, overlayRootRef.current)
              : overlay;
          })()}

        <div className="panel-scrollable-content">
          {isLoading &&
          kochOrigins.length === 0 &&
          competitorOrigins.length === 0 ? null : (
            <>
              {(filterType === "all" || filterType === "koch") &&
                renderOriginSection(
                  "koch",
                  kochOrigins,
                  isAddingKoch,
                  setIsAddingKoch,
                )}

              {(filterType === "all" || filterType === "competitor") &&
                renderOriginSection(
                  "competitor",
                  competitorOrigins,
                  isAddingCompetitor,
                  setIsAddingCompetitor,
                )}
            </>
          )}
        </div>

        <FooterMultiLocation
          selected="origins"
          onSelect={async (item) => {
            flushAllOriginRows();

            if (item === "account") {
              const state = useNitroStore.getState();
              state.setShowSettingsPanel?.(true);
              state.setShowMobileFavoriteOriginPanel?.(false);
              state.setShowMobileSearchPanel?.(false);
            }

            if (item === "quote" || item === "account") {
              await awaitPendingRequestsBeforeNavigate();
              if (onClose) {
                onClose();
              }
              if (onFooterSelect) {
                onFooterSelect(item);
              }
              return;
            }

            if (onFooterSelect) {
              onFooterSelect(item);
            }
          }}
        />
        {!shouldHideSubFooter && (
          <MobileSubFooter
            onClose={onClose}
            onCloseAll={onCloseAll}
            onMapClick={async () => {
              flushAllOriginRows();
              await awaitPendingRequestsBeforeNavigate();
            }}
            onProductSelectClick={flushAllOriginRows}
            onMonthSelectClick={flushAllOriginRows}
            forceMapButton
            selectedMonth={selectedMonth}
            setSelectedMonth={(value) => {
              if (setSelectedMonth) setSelectedMonth(value);
            }}
            onBeforeProductChange={awaitPendingRequestsBeforeNavigate}
            onBeforeListClick={awaitPendingRequestsBeforeNavigate}
            splitMonthKeys={splitMonthKeys}
          />
        )}
      </div>
    </div>
  );
};

export default MobileSplitFavoriteOriginsPanel;
